/**
 * useReadingTracker
 *
 * A React hook that manages a WebSocket-based reading session against the
 * Laravel Reverb backend.
 *
 * Features:
 *  - Establishes a private Echo channel (`reading.{zlUid}`) on `startSession`
 *  - Sends an initial `client-read-tick` whisper as soon as the connection
 *    is confirmed
 *  - Automatically whispers a tick every `tickIntervalMs` ms (default 30 s)
 *    while the connection is live
 *  - Handles `RewardUnlocked`, `SessionAutoClosed`, and `TickRejected` server
 *    broadcast events
 *  - Gracefully ends the session (whisper + disconnect) on `stopSession` or
 *    component unmount
 *
 * Usage:
 *   const { startSession, stopSession, isConnected, currentPage } =
 *     useReadingTracker({
 *       zlUid: 'user_abc123',
 *       bookId: 'book-101',
 *       bookTitle: 'Rich Dad Poor Dad',
 *       token: authToken,           // omit to use cookie auth
 *       onRewardUnlocked: (data) => showConfetti(data),
 *     });
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { initEcho } from '@/config/echo';

import {
  ReadingTrackerOptions,
  ReadingTrackerResult,
  ReadTickPayload,
  RewardUnlockedEvent,
  SessionAutoClosedEvent,
  SessionEndReason,
  TickRejectedEvent,
} from '@/types/websocket';

// Re-export for convenience
export type {
  ReadingTrackerOptions,
  ReadingTrackerResult,
} from '@/types/websocket';

const DEFAULT_TICK_INTERVAL_MS = 30_000; // 30 seconds
const DEFAULT_ACTIVE_SECONDS = 30; // satisfies the 5–65 backend constraint

/**
 * Custom hook to manage a WebSocket reading-tracker session.
 */
export const useReadingTracker = ({
  zlUid,
  sourceId,
  sourceName,
  token,
  subSource,
  source,
  sourceType,
  subSourceId,
  subSourceName,
  tickIntervalMs = DEFAULT_TICK_INTERVAL_MS,
  onRewardUnlocked,
  onSessionAutoClosed,
  onTickRejected,
  currentPage,
  getPagesRead,
}: ReadingTrackerOptions): ReadingTrackerResult => {
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Stable refs — never trigger re-renders
  const activeSessionIdRef = useRef<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channelRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const echoRef = useRef<any>(null);
  // Track current page in a ref so sendTick closure always reads latest value
  // Mirror isConnected in a ref so sendTick can read the latest value
  // without being a stale closure (avoids the race on the 'connected' callback).
  const isConnectedRef = useRef<boolean>(false);
  // Keep currentPage in a ref so sendTick always reads the latest page without
  // needing to be added to the useCallback dependency array (which would reset
  // the auto-tick interval on every page change).
  const currentPageRef = useRef<string | undefined>(currentPage);
  // Stable ref to the caller-supplied getPagesRead callback so sendTick always
  // invokes the latest version without being added to its dependency array.
  const getPagesReadRef = useRef<(() => number) | undefined>(getPagesRead);
  useEffect(() => {
    getPagesReadRef.current = getPagesRead;
  });

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // ─── sendTick ─────────────────────────────────────────────────────────────

  /**
   * Whispers a `client-read-tick` event to the private channel.
   *
   * @param sessionId     The active session UUID.
   * @param pagesRead     Pages read in this interval.
   * @param activeSeconds Active reading seconds (must be 5–65).
   * @param isFirst       Whether this is the first tick of the session.
   */
  const sendTick = useCallback(
    (sessionId: string, activeSeconds: number, isFirst = false) => {
      console.info(isFirst);
      if (!channelRef.current || !isConnectedRef.current) return;

      // Delegate page counting to the caller so deduplication lives outside
      // the hook. Fall back to 0 if no callback was provided.
      const pagesRead = getPagesReadRef.current?.() ?? 0;

      const payload: ReadTickPayload = {
        session_id: sessionId,
        source_id: sourceId,
        source: source,
        source_type: sourceType,
        source_name: sourceName,
        sub_source: subSource,
        sub_source_id: subSourceId,
        sub_source_name: subSourceName,
        active_seconds: activeSeconds,
        pages_read: pagesRead,
        current_page: currentPageRef.current,
        platform: 'website',
        zl_uid: zlUid,
        client_timestamp: new Date().toISOString(),
      };

      // Echo automatically prefixes whisper events with 'client-'
      // so this becomes the 'client-read-tick' frame on the wire.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      channelRef.current.whisper('read-tick', payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sourceId,
      sourceName,
      source,
      sourceType,
      subSourceId,
      subSourceName,
      zlUid,
    ],
  );

  // ─── stopSession ──────────────────────────────────────────────────────────

  /**
   * Ends the active reading session gracefully.
   * Sends a `client-session-end` whisper (unless the server already closed it),
   * then disconnects Echo and resets all refs.
   */
  const stopSession = useCallback(
    (reason: SessionEndReason = 'user_closed') => {
      const sessionId = activeSessionIdRef.current;
      if (!sessionId) return;

      if (channelRef.current && isConnected && reason !== 'auto_closed') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        channelRef.current.whisper('client-session-end', {
          session_id: sessionId,
          zl_uid: zlUid,
          reason,
        });
      }

      if (echoRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        echoRef.current.disconnect();
      }

      activeSessionIdRef.current = null;
      channelRef.current = null;
      echoRef.current = null;
      isConnectedRef.current = false;
      setIsConnected(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zlUid],
  );

  // ─── startSession ─────────────────────────────────────────────────────────

  /**
   * Establishes a new reading session:
   *  1. Generates a new UUID session ID
   *  2. Initialises Laravel Echo
   *  3. Subscribes to the private `reading.{zlUid}` channel
   *  4. Binds connection and server-broadcast listeners
   */
  const startSession = useCallback(() => {
    // Prevent duplicate sessions
    if (activeSessionIdRef.current) return;

    activeSessionIdRef.current = uuidv4();
    const echo = initEcho(token);
    if (!echo) {
      console.warn('[ReadingTracker] Echo init failed (server-side context?)');
      return;
    }
    echoRef.current = echo;

    // Subscribe to the user's private channel
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const channel = echo.private(`reading.${zlUid}`);
    channelRef.current = channel;

    // ── Connection established ──────────────────────────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    echo.connector.pusher.connection.bind('connected', () => {
      // Update ref first so sendTick guard passes before React re-renders.
      isConnectedRef.current = true;
      setIsConnected(true);

      // Fire first tick immediately to create the session on the backend.
      // We read the sessionId from the ref to avoid any stale closure.
      if (activeSessionIdRef.current) {
        sendTick(activeSessionIdRef.current, DEFAULT_ACTIVE_SECONDS, true);
      }
    });

    // ── Server broadcast: RewardUnlocked ───────────────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    channel.listen('.RewardUnlocked', (data: RewardUnlockedEvent) => {
      onRewardUnlocked?.(data);
    });

    // ── Server broadcast: SessionAutoClosed ────────────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    channel.listen('.SessionAutoClosed', (data: SessionAutoClosedEvent) => {
      console.warn('[ReadingTracker] ⚠️ Session auto-closed by server', data);
      onSessionAutoClosed?.(data);
      stopSession('auto_closed');
    });

    // ── Server broadcast: TickRejected ─────────────────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    channel.listen('.TickRejected', (data: TickRejectedEvent) => {
      console.warn('[ReadingTracker] ❌ Tick rejected:', data.reason, data);
      onTickRejected?.(data);
    });
  }, [
    token,
    zlUid,
    sendTick,
    stopSession,
    onRewardUnlocked,
    onSessionAutoClosed,
    onTickRejected,
  ]);

  // ─── Auto-tick effect ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!isConnected || !activeSessionIdRef.current) return;

    const interval = setInterval(() => {
      if (activeSessionIdRef.current) {
        sendTick(activeSessionIdRef.current, DEFAULT_ACTIVE_SECONDS, false);
      }
    }, tickIntervalMs);

    return () => clearInterval(interval);
  }, [isConnected, sendTick, tickIntervalMs]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopSession('user_closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    startSession,
    stopSession,
    isConnected,
    activeSessionId: activeSessionIdRef.current,
  };
};
